import React, { useContext, useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Button, Grid, Typography } from "@mui/material";
import { Link, useLocation, useParams } from "react-router-dom";

import { BulkRow } from "@safetyculture/s12-apis-connect-web/s12/directory/v1/directory_pb";

import { UrlParamTypes } from "../types/org-display";
import { BulkDownloadFoldersAPI } from "api/endpoints/sites";
import { callOptionsHeaders } from "api/helpers";
import OrganisationSitesTable from "components/sites/org-sites-table";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

type LocationState = {
  orgName: string;
};

const OrganisationSites = () => {
  const location = useLocation<LocationState>();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { orgName } = location.state || { orgName: "" };
  const [isLoading, setIsLoading] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [sitesList, setSitesList] = useState<BulkRow[]>([]);

  const { showError } = useContext(SnackbarContext);

  const bulkDownloadFolders = async (
    orgId: string,
    includeDeleted: boolean,
  ) => {
    setIsLoading(true);
    return BulkDownloadFoldersAPI(
      {
        OrgId: toUUID(orgId),
        IncludeDeleted: includeDeleted,
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((res) => {
        setSitesList(res.row);
        setIsLoading(false);
      })
      .catch((error) => {
        showError(error);
        setIsLoading(false);
      });
  };

  //Used to allow data table to export the correct fields
  const transformForDataTable = (sitesList: BulkRow[]): BulkRowDataTable[] => {
    return sitesList.map((site) => {
      const hierarchy = site.hierarchy || [];
      return {
        ...site,
        country: hierarchy[4],
        state: hierarchy[3],
        region: hierarchy[2],
        area: hierarchy[1],
        site: hierarchy[0],
      };
    });
  };

  const handleIncludeDeletedToggle = (oldDeletedStatus: boolean) => {
    setIncludeDeleted(!oldDeletedStatus);
    bulkDownloadFolders(roleId, !oldDeletedStatus);
  };

  useEffect(() => {
    bulkDownloadFolders(roleId, includeDeleted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: "20px" }}>
          <Grid container alignContent="space-between" alignItems="center">
            <Grid item>
              <Link
                to={`/organisation/${regionCode}/${roleId}`}
                className="remove-link-style"
                style={{ position: "relative" }}
                data-testid="back-button"
              >
                <Button startIcon={<ArrowBackIcon />} color="primary">
                  Back
                </Button>
              </Link>
            </Grid>

            <Grid item xs={11}>
              <Typography
                variant="h5"
                color="textPrimary"
                gutterBottom
                data-testid="organisation-sites-title"
              >
                {orgName && `${orgName} - `}
                Sites
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <OrganisationSitesTable
        sitesList={transformForDataTable(sitesList)}
        isLoading={isLoading}
        includeDeleted={includeDeleted}
        handleIncludeDeletedToggle={handleIncludeDeletedToggle}
      />
    </>
  );
};
export default OrganisationSites;

export declare class BulkRowDataTable {
  folderId: string;
  memberId: string[];
  hierarchy: string[];
  name: string;
  parentId: string;
  deleted: boolean;

  //Added for data-table-field-exports
  country: string;
  state: string;
  region: string;
  area: string;
  site: string;
}
